import {
  STAGE_EXAMS,
  COURSE_CERTIFICATE_MAX_STAGE,
  listCertificateClasses,
  type ProgressState,
} from "@piano-tutor/shared";

export interface CertificateEligibility {
  eligible: boolean;
  reasons: string[];
  studentName: string;
  certificateId: string;
  completionDate: string;
  classesCompleted: number;
  classesTotal: number;
  examsPassed: number;
  examsTotal: number;
}

export function evaluateCertificateEligibility(opts: {
  displayName: string;
  isSignedIn: boolean;
  isAnonymous: boolean;
  plan: "free" | "full_course";
  progress: ProgressState;
}): CertificateEligibility {
  const reasons: string[] = [];
  if (!opts.isSignedIn || opts.isAnonymous) {
    reasons.push("Sign in with Google (not Guest).");
  }
  if (opts.plan !== "full_course") {
    reasons.push("Own the Full Course.");
  }
  const courseClasses = listCertificateClasses();
  const classesTotal = courseClasses.length;
  const classesCompleted = opts.progress.completedClassIds.filter((id) =>
    courseClasses.some((c) => c.id === id),
  ).length;
  if (classesCompleted < classesTotal) {
    reasons.push(
      `Complete all ${classesTotal} course classes (${classesCompleted}/${classesTotal}).`,
    );
  }
  const certExams = STAGE_EXAMS.filter((e) => e.stage <= COURSE_CERTIFICATE_MAX_STAGE);
  const examsTotal = certExams.length;
  const examsPassed = opts.progress.passedExamIds.filter((id) =>
    certExams.some((e) => e.id === id),
  ).length;
  if (examsPassed < examsTotal) {
    reasons.push(`Pass all ${examsTotal} Gatekeeper exams (${examsPassed}/${examsTotal}).`);
  }

  const studentName = opts.displayName.trim() || "Piano Student";
  const completionDate = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());

  return {
    eligible: reasons.length === 0,
    reasons,
    studentName,
    // IDs are never generated in the browser. The Cloud Function issues a
    // cryptographically random, immutable ID after server-verified exams.
    certificateId: "",
    completionDate,
    classesCompleted,
    classesTotal,
    examsPassed,
    examsTotal,
  };
}

export function printCertificateSheet(sheet: HTMLElement, title: string): void {
  const win = window.open("", "_blank", "noopener,noreferrer,width=960,height=720");
  if (!win) return;
  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((el) => el.outerHTML)
    .join("\n");
  win.document.write(`<!doctype html><html><head><title>${title}</title>${styles}
    <style>
      body { background: #f4f1ea; margin: 0; padding: 1.5rem; }
      .certificate-sample-watermark { display: none !important; }
      .certificate-dialog-bar, .certificate-rules { display: none !important; }
      @media print { body { padding: 0; } }
    </style>
  </head><body>${sheet.outerHTML}
  <script>window.onload=()=>{window.print();}</script>
  </body></html>`);
  win.document.close();
}
